<template>
    <div class="h-fit flex flex-col min-w-[900px] p-2 relative w-full" :data-cy="TransactionManagerCy.Root">
        <file-uploader
            v-model="attachments"
            variant="grid"
            class="mb-2 mt-6"
            @upload-success="emitter.emit(FOCUS_FIRST_TRANS_LINE)"
        />
        <div v-if="resourceLinks">
            <resource-link v-for="resourceLink in resourceLinks" :key="resourceLink.identifier" :urn="resourceLink"/>
        </div>
        <dynamic-transaction-fields-controller/>
        <div
            :style="gridTemplateCols"
            class="grid w-full gap"
        >
            <div
                v-for="field in primaryFields"
                :key="field.key"
                :style="field.style || {}"
                :title="field.label"
                class="text-left whitespace-nowrap truncate text-xs"
            >
                {{ field.label }}
            </div>
            <div/> <!-- column header for action buttons -->
            <create-transaction-line
                v-for="(line, index) in transactionEntryLines"
                :key="line.entryUuid"
                ref="entries"
                :index="index"
                :line="line"
                @update="updatedLine => updateLineAtIndex(index, updatedLine)"
                @enter-on-last="enterOnLastCell(index)"
                @handle-copy-line="handleCopyLine"
                @handle-new-blank-line="handleNewBlankLine"
                @handle-delete-line-at-index="handleDeleteLineAtIndex"
            />
        </div>

        <div class="bg-primary/20 rounded text-sm mb-6 mt-2 border border-base-300 shadow-md">
            <div class="grid grid-cols-7 gap-x-1 px-3 py-0.5 text-xs">
                <div-with-label
                    label="Debet"
                >
                    <template #text>
                        <div :class="balanced ? '' : 'text-error'">
                            {{ formatCurrency(sumDebit) }}
                        </div>
                    </template>
                    <template #extraText>
                        <div v-if="balanceDifference" :class="balanced ? '' : 'text-error'"> ({{ balanceDifference > 0 ? '+' : '-' }} {{ formatCurrency(Math.abs(balanceDifference)) }})</div>
                    </template>
                </div-with-label>

                <div-with-label
                    label="MVA"
                >
                    <template #text>
                        {{ formatCurrency(vatAmounts.debit) }}
                    </template>
                </div-with-label>

                <div-with-label
                    label="Kredit"
                >
                    <template #text>
                        <div :class="balanced ? '' : 'text-error'">
                            {{ formatCurrency(sumCredit) }}
                        </div>
                    </template>
                    <template #extraText>
                        <div v-if="balanceDifference" :class="balanced ? '' : 'text-error'"> ({{ balanceDifference > 0 ? '-' : '+' }} {{ formatCurrency(Math.abs(balanceDifference)) }})</div>
                    </template>
                </div-with-label>

                <div-with-label
                    label="MVA"
                >
                    <template #text>
                        {{ formatCurrency(vatAmounts.credit) }}
                    </template>
                </div-with-label>
                <div-with-label
                    label="Differanse"
                >
                    <template #text>
                        <div :class="balanced ? '' : 'text-error'">
                            {{ formatCurrency(difference) }}
                        </div>
                    </template>
                </div-with-label>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import {
    copyLine,
    deleteLineAtIndex,
    moveFocusBetweenTransactions,
    newBlankLine,
    updateLineAtIndex,
    useTransactionCreator
} from '@/transaction/create-transaction/TransactionCreator.service'
import {CreateTransEntryInstances} from '@/transaction/create-transaction/QuickTransEntriesInstances'
import {LINE_COLUMN} from '@/transaction/create-transaction/EntryLineInstance'
import DivWithLabel from '@/components/DivWithLabel.vue'
import FileUploader from '@/document/FileUploader.vue'
import {floatsAreEqual} from '@/transaction/create-transaction/EntryLineValidator'
import {formatCurrency} from '@/services/formatter.service'
import {hasSelectedOrg, orgSelectionComplete} from '@/tenants/org.service'
import {TRANSACTION_DRAFT_DELETED, TRANSACTION_DRAFT_LOADED} from '@/types/transaction/TransactionEvent'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {FOCUS_FIRST_TRANS_LINE, ORG_SWITCHED} from '@/types/events'
import CreateTransactionLine from '@/transaction/create-transaction/CreateTransactionLine.vue'
import useDynamicTransactionFields, {TransactionInputField} from './transaction-fields.controller'
import DynamicTransactionFieldsController from '@/transaction/create-transaction/DynamicTransactionFieldsController.vue'
import {draftUuid} from '@/transaction-journal/transaction.draft.service'
import ResourceLink from '@/resource-link/ResourceLink.vue'
import {TransactionManagerCy} from '@/transaction/TransactionManagerCy'

const {primaryFields} = useDynamicTransactionFields()
const {onEvent, cleanUp} = useEmitter()
const {transactionEntryLines, attachments, reset: resetCreate, vatAmounts, resourceLinks} = useTransactionCreator()

const entries: CreateTransEntryInstances = ref<Array<InstanceType<typeof CreateTransactionLine>>>([])

const gridTemplateCols = computed(() => {
    return 'grid-template-columns: ' + primaryFields.value.map((field: TransactionInputField) => {
        const min = field.style?.minWidth || 'auto'
        const max = field.style?.maxWidth || 'auto'
        return field.style?.width || `minmax(${min}, ${max})`
    }).join(' ') + ' auto'
})

const sumDebit = computed(() => {
    return transactionEntryLines.value.reduce((sum, line) => {
        return sum + (line.debitAccount ? Number(line.amount) : 0)
    }, 0)
})

const sumCredit = computed(() => {
    return transactionEntryLines.value.reduce((sum, line) => {
        return sum + (line.creditAccount ? Number(line.amount) : 0)
    }, 0)
})

const difference = computed(() => {
    return (sumDebit.value - sumCredit.value)
})

const balanced = computed(() => {
    return sumDebit.value === sumCredit.value
})

const balanceDifference = computed(() => {
    if (!floatsAreEqual(sumDebit.value, sumCredit.value)) {
        return sumDebit.value - sumCredit.value
    }
    return 0
})

onEvent(ORG_SWITCHED, resetCreate)

onEvent(TRANSACTION_DRAFT_LOADED, (uuid) => {
    draftUuid.value = uuid as string
})

onEvent(TRANSACTION_DRAFT_DELETED, () => {
    draftUuid.value = undefined
})

onEvent(FOCUS_FIRST_TRANS_LINE, () => {
    focusOnFirstLine()
})

onMounted(async () => {
    await orgSelectionComplete
    if (!hasSelectedOrg()) {
        console.error('Accessing the accounting page without selected org. Should not be possible, this is a bug!')
    }
})

onUnmounted(() => {
    cleanUp()
})

const enterOnLastCell = (index: number) => {
    if (index === transactionEntryLines.value.length - 1) {
        newBlankLine(index + 1)
    }
    moveFocusBetweenTransactions(index, LINE_COLUMN.Date, entries)
}

const focusOnFirstLine = () => {
    if (entries.value && entries.value.length > 0) {
        entries.value[0].moveFocusToColumn(LINE_COLUMN.Date, 0)
    }
}

const handleCopyLine = (line: TransactionEntryLine, index: number) => {
    copyLine(line, index + 1)
    moveFocusBetweenTransactions(index, LINE_COLUMN.Date, entries)
}

const handleNewBlankLine = (index: number) => {
    newBlankLine(index + 1)
    moveFocusBetweenTransactions(index, LINE_COLUMN.Date, entries)
}

const handleDeleteLineAtIndex = (index: number) => {
    deleteLineAtIndex(index)
}
</script>


<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.label-grid {
    @apply grid px-2 gap-x-2;
    grid-template-columns: 100px minmax(170px, 100%) minmax(80px, 100%) minmax(170px, 100%) minmax(80px, 100%) 200px;
}

.summary-line-grid {
    @apply grid px-2 gap-x-2;
    grid-template-columns: 100px minmax(170px, 100%) minmax(80px, 100%) minmax(170px, 100%) minmax(80px, 100%) 90px 110px;
}

</style>